<script>
  // Settings hub. Houses several admin-only sub-areas: alert dispatch
  // (SMTP2GO) and the user access whitelist. Users is rendered here as a
  // sub-section so the rail stays focused on the main destinations -- the
  // standalone /users URL still works for direct links.
  import { onMount } from 'svelte';
  import { api } from '../lib/api.js';
  import { ui } from '../lib/ui.svelte.js';
  import { getUser } from '../lib/auth.js';
  import TextField from '../components/TextField.svelte';
  import Users from './Users.svelte';

  const me = getUser();
  const isAdmin = me?.portal_role === 'admin';

  // Sub-tabs. Defaults to "alerts"; a future "Users" tab link from the
  // sidebar or a quick link inside the user menu can deep-link via
  // setActive(). The Users tab seeds from ?tab=users for shareability.
  const tabs = [
    { id: 'alerts',   label: 'Alerts',      icon: 'notifications_active' },
    { id: 'delivery', label: 'Email probe', icon: 'mark_email_read' },
    { id: 'users',    label: 'Users',       icon: 'group' },
  ];
  function seedTab() {
    if (typeof window === 'undefined') return 'alerts';
    const u = new URLSearchParams(window.location.search);
    const t = u.get('tab');
    return tabs.some((x) => x.id === t) ? t : 'alerts';
  }
  let activeTab = $state(seedTab());
  function setActive(id) {
    activeTab = id;
    const u = new URLSearchParams(window.location.search);
    if (id === 'alerts') u.delete('tab'); else u.set('tab', id);
    const qs = u.toString();
    history.replaceState(null, '', '/settings' + (qs ? '?' + qs : ''));
  }

  // ---- Alerts panel ---------------------------------------------------
  let smtp2goKey = $state('');
  let keyRegistered = $state(false); // server has a key; field stays blank
  let alertFrom  = $state('');
  let alertEmailsText = $state('');
  let saving = $state(false);
  let testing = $state(false);
  let loaded = $state(false);
  let alertsEditing = $state(false); // false = show the summary card; true = show the edit form

  async function load() {
    try {
      const c = await api.getAlertSettings();
      // The API never returns the key -- only whether one is registered. Leave
      // the field blank; an empty key on save/test means "keep/use the saved one".
      smtp2goKey = '';
      keyRegistered = !!c?.smtp2go_api_key_set;
      alertFrom  = c?.alert_from || '';
      alertEmailsText = (c?.alert_emails || []).join('\n');
    } catch (e) { ui.snack(e.message, 'error'); }
    finally { loaded = true; }
  }

  function parseRecipients() {
    return alertEmailsText.split(/[\n,]/).map((s) => s.trim()).filter(Boolean);
  }

  async function save() {
    saving = true;
    try {
      await api.putAlertSettings({
        smtp2go_api_key: smtp2goKey.trim(),
        alert_from: alertFrom.trim(),
        alert_emails: parseRecipients(),
      });
      ui.snack('Settings saved', 'success');
      alertsEditing = false;
      await load(); // refresh the "registered" state and clear the key field
    } catch (e) { ui.snack(e.message, 'error'); }
    finally { saving = false; }
  }

  async function sendTest() {
    testing = true;
    try {
      await api.testAlertSettings({
        smtp2go_api_key: smtp2goKey.trim(),
        alert_from: alertFrom.trim(),
        alert_emails: parseRecipients(),
      });
      ui.snack('Test email sent', 'success');
    } catch (e) { ui.snack(e.message, 'error'); }
    finally { testing = false; }
  }

  // ---- Email probe panel ----------------------------------------------
  // Outbound deliverability monitor: the binary sends a tokenised email via
  // SMTP2GO every interval and reads the mailbox back over IMAP to time the
  // end-to-end delivery. SMTP2GO key/sender come from the Alerts tab.
  let pEnabled  = $state(false);
  let pInterval = $state('5');   // minutes (UI); stored as seconds
  let pTo       = $state('');
  let pImapHost = $state('imap.gmail.com');
  let pImapPort = $state('993');
  let pImapUser = $state('');
  let pImapPass = $state('');
  let pPassRegistered = $state(false); // server has an App Password; field stays blank
  let pImapBox  = $state('INBOX');
  let pUnique   = $state(true);   // Gmail +tag unique recipient per send
  let pAlertEnabled = $state(false);
  let pAlertSeconds = $state('20'); // degraded threshold (UI seconds; stored ms)
  let pAlertEmailsText = $state('');
  let pSaving = $state(false);
  let pTesting = $state(false);
  let pLoaded = $state(false);
  let imapEditing = $state(false); // false = summary card; true = edit form
  let probes = $state([]);
  let probesLoading = $state(false);
  // Recent-probes table selection (mirrors the Captures table).
  let pSelected = $state(new Set());
  let pLastSel = $state(-1);

  async function loadProbe() {
    try {
      const c = await api.getProbeSettings();
      pEnabled  = !!c?.enabled;
      pInterval = String(Math.max(1, Math.round((c?.interval_seconds || 300) / 60)));
      pTo       = c?.to || '';
      pImapHost = c?.imap_host || 'imap.gmail.com';
      pImapPort = String(c?.imap_port || 993);
      pImapUser = c?.imap_username || '';
      // Password is never returned -- only whether one is registered.
      pImapPass = '';
      pPassRegistered = !!c?.imap_password_set;
      pImapBox  = c?.imap_mailbox || 'INBOX';
      pUnique   = c?.unique_plus_tag !== false; // default on
      pAlertEnabled = !!c?.alert_enabled;
      pAlertSeconds = String(Math.max(1, Math.round((c?.alert_latency_ms || 20000) / 1000)));
      pAlertEmailsText = (c?.alert_emails || []).join('\n');
    } catch (e) { ui.snack(e.message, 'error'); }
    finally { pLoaded = true; }
    refreshProbes();
  }

  function parseProbeAlertEmails() {
    return pAlertEmailsText.split(/[\n,]/).map((s) => s.trim()).filter(Boolean);
  }

  function probePayload() {
    return {
      enabled: pEnabled,
      interval_seconds: Math.max(60, (Number(pInterval) || 5) * 60),
      to: pTo.trim(),
      imap_host: pImapHost.trim(),
      imap_port: Number(pImapPort) || 993,
      imap_username: pImapUser.trim(),
      imap_password: pImapPass,
      imap_mailbox: pImapBox.trim() || 'INBOX',
      unique_plus_tag: pUnique,
      alert_enabled: pAlertEnabled,
      alert_emails: parseProbeAlertEmails(),
      alert_latency_ms: Math.max(1000, (Number(pAlertSeconds) || 20) * 1000),
    };
  }

  async function saveProbe() {
    pSaving = true;
    try {
      await api.putProbeSettings(probePayload());
      ui.snack('Email probe settings saved', 'success');
      imapEditing = false;
      await loadProbe(); // refresh the "registered" state and clear the password field
    } catch (e) { ui.snack(e.message, 'error'); }
    finally { pSaving = false; }
  }

  async function testImap() {
    pTesting = true;
    try {
      const r = await api.testProbeImap(probePayload());
      ui.snack(`IMAP connected — ${r?.messages ?? 0} message(s) in ${pImapBox.trim() || 'INBOX'}`, 'success');
    } catch (e) { ui.snack(e.message, 'error'); }
    finally { pTesting = false; }
  }

  async function refreshProbes() {
    probesLoading = true;
    try {
      probes = await api.listEmailProbes(100) || [];
    } catch (e) { ui.snack(e.message, 'error'); }
    finally { probesLoading = false; }
  }

  function fmtTime(t) {
    if (!t) return '—';
    const d = new Date(t);
    return isNaN(d) ? '—' : d.toLocaleString();
  }
  function fmtMs(ms) {
    if (ms == null) return '—';
    if (ms < 1000) return `${ms} ms`;
    return `${(ms / 1000).toFixed(1)} s`;
  }
  // SMTP2GO event -> badge colour. delivered = ok; bounces/rejections = fail;
  // anything mid-flight (processed/soft-bounced/…) = neutral.
  function providerBadge(status) {
    if (status === 'delivered') return 'probe-badge-ok';
    if (['hard-bounced', 'rejected', 'spam'].includes(status)) return 'probe-badge-fail';
    return 'probe-badge-wait';
  }

  // --- Recent-probes selection + export --------------------------------
  // Click toggles one; shift-click selects the range from the last-clicked row
  // to this one. onclick (not onchange) so we capture shiftKey -- same as the
  // Captures table.
  function toggleProbeSelect(index, shiftKey) {
    const next = new Set(pSelected);
    if (shiftKey && pLastSel >= 0) {
      const lo = Math.min(pLastSel, index), hi = Math.max(pLastSel, index);
      for (let i = lo; i <= hi; i++) next.add(probes[i].id);
    } else {
      const id = probes[index].id;
      if (next.has(id)) next.delete(id); else next.add(id);
    }
    pSelected = next;
    pLastSel = index;
  }
  let allProbesSelected = $derived(probes.length > 0 && probes.every((p) => pSelected.has(p.id)));
  function selectAllProbes() {
    pSelected = allProbesSelected ? new Set() : new Set(probes.map((p) => p.id));
  }
  function clearProbeSelection() { pSelected = new Set(); pLastSel = -1; }

  function stamp() { return new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19); }
  function saveJson(data, filename) {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }
  function selectedProbeDocs() { return probes.filter((p) => pSelected.has(p.id)); }

  async function copySelectedProbes() {
    const docs = selectedProbeDocs();
    if (!docs.length) return;
    try {
      await navigator.clipboard.writeText(JSON.stringify(docs, null, 2));
      ui.snack(`Copied ${docs.length.toLocaleString()} probe${docs.length === 1 ? '' : 's'}`, 'success', 1500);
    } catch (e) { ui.snack('Copy failed: ' + e.message, 'error'); }
  }
  function downloadSelectedProbes() {
    const docs = selectedProbeDocs();
    if (!docs.length) return;
    saveJson(docs, `email-probes-selected-${stamp()}.json`);
    ui.snack(`Downloaded ${docs.length.toLocaleString()} probe${docs.length === 1 ? '' : 's'}`, 'success');
  }
  function downloadAllProbes() {
    if (!probes.length) return;
    saveJson(probes, `email-probes-${stamp()}.json`);
    ui.snack(`Downloaded ${probes.length.toLocaleString()} probe${probes.length === 1 ? '' : 's'}`, 'success');
  }

  // CSV export. Fixed column order; values are quoted only when they contain a
  // comma, quote, or newline (RFC-4180 style).
  const CSV_COLS = ['id','token','send_type','sent_at','sender','to','send_ms','received_at','delivery_ms','smtp2go_email_id','smtp2go_status','smtp2go_opened_at','smtp2go_response','send_ok','send_error'];
  function csvEscape(v) {
    if (v == null) return '';
    const s = String(v);
    return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
  }
  function probesToCsv(rows) {
    const head = CSV_COLS.join(',');
    const body = rows.map((r) => CSV_COLS.map((c) => csvEscape(r[c])).join(','));
    return [head, ...body].join('\n');
  }
  function saveCsv(rows, filename) {
    const blob = new Blob([probesToCsv(rows)], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  }
  function downloadSelectedCsv() {
    const docs = selectedProbeDocs();
    if (!docs.length) return;
    saveCsv(docs, `email-probes-selected-${stamp()}.csv`);
    ui.snack(`Downloaded ${docs.length.toLocaleString()} probe${docs.length === 1 ? '' : 's'} (CSV)`, 'success');
  }

  // --- Per-row context menu (long-press on touch, right-click on desktop) ---
  let rowMenu = $state({ open: false, x: 0, y: 0, probe: null });
  let lpTimer = null;
  function openRowMenu(x, y, p) {
    const mw = 200, mh = 188;
    const cx = Math.max(8, Math.min(x, window.innerWidth - mw - 8));
    const cy = Math.max(8, Math.min(y, window.innerHeight - mh - 8));
    rowMenu = { open: true, x: cx, y: cy, probe: p };
  }
  function closeRowMenu() { rowMenu = { open: false, x: 0, y: 0, probe: null }; }
  function onRowContextMenu(e, p) { e.preventDefault(); openRowMenu(e.clientX, e.clientY, p); }
  function onRowTouchStart(e, p) {
    const t = e.touches?.[0]; if (!t) return;
    const x = t.clientX, y = t.clientY;
    clearTimeout(lpTimer);
    lpTimer = setTimeout(() => openRowMenu(x, y, p), 500);
  }
  function cancelLongPress() { clearTimeout(lpTimer); lpTimer = null; }

  async function copyRowAs(p, fmt) {
    if (!p) return;
    const text = fmt === 'csv' ? probesToCsv([p]) : JSON.stringify(p, null, 2);
    try {
      await navigator.clipboard.writeText(text);
      ui.snack(`Copied log (${fmt.toUpperCase()})`, 'success', 1500);
    } catch (e) { ui.snack('Copy failed: ' + e.message, 'error'); }
    closeRowMenu();
  }
  function downloadRowAs(p, fmt) {
    if (!p) return;
    const base = `email-probe-${(p.token || p.id || 'log').slice(0, 12)}-${stamp()}`;
    if (fmt === 'csv') saveCsv([p], `${base}.csv`); else saveJson(p, `${base}.json`);
    ui.snack(`Downloaded log (${fmt.toUpperCase()})`, 'success');
    closeRowMenu();
  }

  // Lazy-load the probe panel the first time its tab is opened (incl. a direct
  // ?tab=delivery deep link).
  $effect(() => {
    if (isAdmin && activeTab === 'delivery' && !pLoaded) loadProbe();
  });

  onMount(() => { if (isAdmin) load(); });
</script>

{#if !isAdmin}
  <div class="bg-surface rounded-2xl p-12 text-center text-on-surface-variant">
    <span class="material-symbols-outlined text-5xl mb-2">lock</span>
    <p>Admin access is required to view or edit settings.</p>
  </div>
{:else}
  <!-- Sub-nav tabs. Material 3 secondary tabs: pill-shaped, primary-tinted
       indicator on the active tab. -->
  <div class="settings-tabs" role="tablist">
    {#each tabs as t}
      <button
        role="tab"
        aria-selected={activeTab === t.id}
        class="settings-tab"
        class:settings-tab-active={activeTab === t.id}
        onclick={() => setActive(t.id)}
      >
        <span class="material-symbols-outlined text-base">{t.icon}</span>
        {t.label}
      </button>
    {/each}
  </div>

  {#if activeTab === 'alerts'}
    {#if !loaded}
      <p class="text-on-surface-variant">Loading...</p>
    {:else}
      <section class="bg-surface rounded-2xl p-5 mb-4">
        <header class="settings-card-head">
          <span class="material-symbols-outlined text-primary">notifications_active</span>
          <div>
            <h3 class="settings-card-title">Alert dispatch</h3>
            <p class="settings-card-sub">
              Sends an email when a status target transitions up→down (or recovers).
              Uses <a href="https://app.smtp2go.com/settings/apikeys/" target="_blank" rel="noopener" class="settings-link">SMTP2GO</a>'s API.
            </p>
          </div>
        </header>

        {#if keyRegistered && !alertsEditing}
          <!-- Summary card: show what's registered without the secret. -->
          <div class="settings-summary">
            <p class="settings-summary-ok">
              <span class="material-symbols-outlined">check_circle</span>
              SMTP2GO key registered
            </p>
            <dl class="settings-summary-dl">
              <div><dt>Sender</dt><dd>{alertFrom || 'hook-index <noreply@telebroad.com>'}</dd></div>
              <div><dt>Recipients</dt><dd>{parseRecipients().length || 0} configured</dd></div>
            </dl>
          </div>
          <div class="settings-actions">
            <button onclick={sendTest} disabled={testing} class="settings-btn-text">
              <span class="material-symbols-outlined text-base align-middle">send</span>
              {testing ? 'Sending…' : 'Send test email'}
            </button>
            <button onclick={() => (alertsEditing = true)} class="settings-btn-primary">
              <span class="material-symbols-outlined text-base align-middle">edit</span>
              Update
            </button>
          </div>
        {:else}
          <div class="settings-grid">
            <div class="settings-grid-wide">
              <TextField label="SMTP2GO API key" type="password" bind:value={smtp2goKey} leadingIcon="key" autocomplete="off" />
              <p class="settings-field-hint">
                {#if keyRegistered}
                  <span class="material-symbols-outlined text-base align-middle">check_circle</span>
                  A key is registered. Leave blank to keep it; type a new key to replace it.
                {:else}
                  No key set yet — paste your SMTP2GO API key.
                {/if}
              </p>
            </div>
            <div class="settings-grid-wide">
              <TextField label='Sender (e.g. "hook-index <noreply@telebroad.com>")' bind:value={alertFrom} leadingIcon="alternate_email" />
            </div>
            <div class="settings-grid-wide">
              <label class="settings-textarea-label">
                <span>Recipients (one per line, or comma-separated)</span>
                <textarea
                  bind:value={alertEmailsText}
                  placeholder="oncall@telebroad.com&#10;ops@telebroad.com"
                  rows="4"
                  class="settings-textarea"
                ></textarea>
              </label>
            </div>
          </div>

          <div class="settings-actions">
            {#if keyRegistered}
              <button onclick={() => { alertsEditing = false; load(); }} disabled={saving} class="settings-btn-text">Cancel</button>
            {/if}
            <button onclick={sendTest} disabled={testing || saving} class="settings-btn-text">
              <span class="material-symbols-outlined text-base align-middle">send</span>
              {testing ? 'Sending…' : 'Send test email'}
            </button>
            <button onclick={save} disabled={saving} class="settings-btn-primary">
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        {/if}
      </section>
    {/if}
  {:else if activeTab === 'delivery'}
    {#if !pLoaded}
      <p class="text-on-surface-variant">Loading...</p>
    {:else}
      <section class="bg-surface rounded-2xl p-5 mb-4">
        <header class="settings-card-head">
          <span class="material-symbols-outlined text-primary">mark_email_read</span>
          <div>
            <h3 class="settings-card-title">Email delivery probe</h3>
            <p class="settings-card-sub">
              Sends a tokenised test email through SMTP2GO on a fixed interval and reads the
              mailbox back over IMAP to time end-to-end delivery. Uses the SMTP2GO key + sender
              from the <button type="button" class="settings-link" onclick={() => setActive('alerts')}>Alerts</button> tab.
            </p>
          </div>
        </header>

        <div class="settings-grid">
          <label class="probe-check settings-grid-wide">
            <input type="checkbox" bind:checked={pEnabled} class="probe-checkbox" />
            <span>Enabled — send a probe every interval</span>
          </label>
          <TextField label="Interval (minutes)" bind:value={pInterval} inputmode="numeric" leadingIcon="schedule" />
          <TextField label="Recipient address" bind:value={pTo} leadingIcon="forward_to_inbox" />
          <label class="probe-check settings-grid-wide">
            <input type="checkbox" bind:checked={pUnique} class="probe-checkbox" />
            <span>Unique recipient per send — append <code>+&lt;token&gt;</code> (Gmail routes it to the same inbox, but SMTP2GO sees a fresh address so the steady probe stream isn't flagged as spam)</span>
          </label>
        </div>

        <div class="settings-actions">
          <button onclick={saveProbe} disabled={pSaving} class="settings-btn-primary">
            {pSaving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </section>

      <section class="bg-surface rounded-2xl p-5 mb-4">
        <header class="settings-card-head">
          <span class="material-symbols-outlined text-primary">crisis_alert</span>
          <div>
            <h3 class="settings-card-title">Delivery alerts</h3>
            <p class="settings-card-sub">
              Emails the subscribers below when delivery degrades — the SMTP2GO API send time or
              actual delivery crosses the threshold, or a send fails — and again when it recovers.
              One alert per transition (not per slow probe).
            </p>
          </div>
        </header>

        <div class="settings-grid">
          <label class="probe-check settings-grid-wide">
            <input type="checkbox" bind:checked={pAlertEnabled} class="probe-checkbox" />
            <span>Enabled — alert on degraded / recovered delivery</span>
          </label>
          <TextField label="Degraded threshold (seconds)" bind:value={pAlertSeconds} inputmode="numeric" leadingIcon="timer" />
          <div class="settings-grid-wide">
            <label class="settings-textarea-label">
              <span>Subscribers (one per line, or comma-separated)</span>
              <textarea
                bind:value={pAlertEmailsText}
                placeholder="oncall@telebroad.com&#10;ops@telebroad.com"
                rows="3"
                class="settings-textarea"
              ></textarea>
            </label>
          </div>
        </div>

        <div class="settings-actions">
          <button onclick={saveProbe} disabled={pSaving} class="settings-btn-primary">
            {pSaving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </section>

      <section class="bg-surface rounded-2xl p-5 mb-4">
        <header class="settings-card-head">
          <span class="material-symbols-outlined text-primary">inbox</span>
          <div>
            <h3 class="settings-card-title">IMAP read-back</h3>
            <p class="settings-card-sub">
              Mailbox the probe logs into to confirm receipt. For Gmail, enable IMAP + 2-Step
              Verification and use a 16-digit <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noopener" class="settings-link">App Password</a>
              (a normal password is rejected). Leave the password blank to log send timing only.
            </p>
          </div>
        </header>

        {#if pPassRegistered && !imapEditing}
          <!-- Summary card: which mailbox is registered, without the password. -->
          <div class="settings-summary">
            <p class="settings-summary-ok">
              <span class="material-symbols-outlined">check_circle</span>
              IMAP connected — App Password registered
            </p>
            <dl class="settings-summary-dl">
              <div><dt>Account</dt><dd>{pImapUser || pTo || '—'}</dd></div>
              <div><dt>Server</dt><dd>{pImapHost}:{pImapPort}</dd></div>
              <div><dt>Mailbox</dt><dd>{pImapBox}</dd></div>
            </dl>
          </div>
          <div class="settings-actions">
            <button onclick={testImap} disabled={pTesting} class="settings-btn-text">
              <span class="material-symbols-outlined text-base align-middle">cable</span>
              {pTesting ? 'Testing…' : 'Test IMAP connection'}
            </button>
            <button onclick={() => (imapEditing = true)} class="settings-btn-primary">
              <span class="material-symbols-outlined text-base align-middle">edit</span>
              Update
            </button>
          </div>
        {:else}
          <div class="settings-grid">
            <TextField label="IMAP host" bind:value={pImapHost} leadingIcon="dns" />
            <TextField label="Port" bind:value={pImapPort} inputmode="numeric" />
            <TextField label="Username (mailbox address)" bind:value={pImapUser} leadingIcon="person" />
            <TextField label="Mailbox" bind:value={pImapBox} leadingIcon="folder" />
            <div class="settings-grid-wide">
              <TextField label="App Password" type="password" bind:value={pImapPass} leadingIcon="key" autocomplete="off" />
              <p class="settings-field-hint">
                {#if pPassRegistered}
                  <span class="material-symbols-outlined text-base align-middle">check_circle</span>
                  A password is registered. Leave blank to keep it; type a new one to replace it.
                {:else}
                  No password set — paste the 16-digit Gmail App Password.
                {/if}
              </p>
            </div>
          </div>

          <div class="settings-actions">
            {#if pPassRegistered}
              <button onclick={() => { imapEditing = false; loadProbe(); }} disabled={pSaving} class="settings-btn-text">Cancel</button>
            {/if}
            <button onclick={testImap} disabled={pTesting || pSaving} class="settings-btn-text">
              <span class="material-symbols-outlined text-base align-middle">cable</span>
              {pTesting ? 'Testing…' : 'Test IMAP connection'}
            </button>
            <button onclick={saveProbe} disabled={pSaving} class="settings-btn-primary">
              {pSaving ? 'Saving…' : 'Save'}
            </button>
          </div>
        {/if}
      </section>

      <section class="bg-surface rounded-2xl p-5 mb-4">
        <header class="settings-card-head">
          <span class="material-symbols-outlined text-primary">history</span>
          <div class="probe-log-head">
            <div>
              <h3 class="settings-card-title">Recent probes</h3>
              <p class="settings-card-sub">
                <b>Send time</b> = how long SMTP2GO's API took to return <code>200 OK</code> (its responsiveness).
                <b>Delivery</b> = time until the message actually landed in the mailbox (arrival − sent).
                These differ when SMTP2GO accepts/delivers fast but its API is slow to acknowledge.
                Click rows to select (shift-click for a range).
              </p>
            </div>
            <div class="probe-log-actions">
              <button onclick={downloadAllProbes} disabled={probes.length === 0} class="settings-btn-text">
                <span class="material-symbols-outlined text-base align-middle">download</span>
                Download
              </button>
              <button onclick={refreshProbes} disabled={probesLoading} class="settings-btn-text">
                <span class="material-symbols-outlined text-base align-middle">refresh</span>
                {probesLoading ? 'Refreshing…' : 'Refresh'}
              </button>
            </div>
          </div>
        </header>

        {#if pSelected.size > 0}
          <div class="probe-selbar">
            <span class="probe-selbar-count">{pSelected.size.toLocaleString()} selected</span>
            <button onclick={copySelectedProbes} class="settings-btn-text">
              <span class="material-symbols-outlined text-base align-middle">content_copy</span>
              Copy JSON
            </button>
            <button onclick={downloadSelectedProbes} class="settings-btn-text">
              <span class="material-symbols-outlined text-base align-middle">download</span>
              JSON
            </button>
            <button onclick={downloadSelectedCsv} class="settings-btn-text">
              <span class="material-symbols-outlined text-base align-middle">download</span>
              CSV
            </button>
            <button onclick={clearProbeSelection} class="settings-btn-text">Clear</button>
          </div>
        {/if}

        {#if probes.length === 0}
          <p class="text-on-surface-variant">No probes recorded yet. Enable the probe and save; the first send fires within ~30s.</p>
        {:else}
          <div class="probe-table-wrap">
            <table class="probe-table">
              <thead>
                <tr>
                  <th class="probe-check-col">
                    <input type="checkbox" checked={allProbesSelected} onchange={selectAllProbes} class="probe-checkbox" aria-label="Select all probes" />
                  </th>
                  <th>Sent</th>
                  <th>Sender</th>
                  <th title="Send channel: http = SMTP2GO v3 API">Type</th>
                  <th>Recipient</th>
                  <th title="How long SMTP2GO's API took to return 200 OK — not delivery time">Send time</th>
                  <th>Received</th>
                  <th title="Time until the message arrived in the mailbox (arrival − sent)">Delivery</th>
                  <th>Provider (SMTP2GO)</th>
                  <th title="First SMTP2GO 'opened' event — needs open-tracking on and a real open in a mail client">Opened</th>
                  <th>Status</th>
                  <th>Error</th>
                  <th>Email ID</th>
                </tr>
              </thead>
              <tbody>
                {#each probes as p, i (p.id)}
                  {@const sel = pSelected.has(p.id)}
                  <tr
                    class:probe-row-selected={sel}
                    oncontextmenu={(e) => onRowContextMenu(e, p)}
                    ontouchstart={(e) => onRowTouchStart(e, p)}
                    ontouchend={cancelLongPress}
                    ontouchmove={cancelLongPress}
                    ontouchcancel={cancelLongPress}
                  >
                    <td class="probe-check-col">
                      <input type="checkbox" checked={sel} onclick={(e) => toggleProbeSelect(i, e.shiftKey)} class="probe-checkbox" aria-label="Select probe" />
                    </td>
                    <td>{fmtTime(p.sent_at)}</td>
                    <td class="probe-sender" title={p.sender || ''}>{p.sender || '—'}</td>
                    <td><span class="probe-type">{p.send_type || 'http'}</span></td>
                    <td class="probe-recipient" title={p.to || ''}>{p.to || '—'}</td>
                    <td>{fmtMs(p.send_ms)}</td>
                    <td>{fmtTime(p.received_at)}</td>
                    <td>{p.delivery_ms != null ? fmtMs(p.delivery_ms) : '—'}</td>
                    <td>
                      {#if p.smtp2go_status}
                        <span class="probe-badge {providerBadge(p.smtp2go_status)}" title={p.smtp2go_response || ''}>{p.smtp2go_status}</span>
                      {:else}
                        <span class="probe-muted">—</span>
                      {/if}
                    </td>
                    <td>{p.smtp2go_opened_at ? fmtTime(p.smtp2go_opened_at) : '—'}</td>
                    <td>
                      {#if !p.send_ok}
                        <span class="probe-badge probe-badge-fail" title={p.send_error || ''}>send failed</span>
                      {:else if p.received_at}
                        <span class="probe-badge probe-badge-ok">delivered</span>
                      {:else}
                        <span class="probe-badge probe-badge-wait">awaiting receipt</span>
                      {/if}
                    </td>
                    <td class="probe-error" title={p.send_error || ''}>
                      {#if p.send_error}<span class="probe-error-text">{p.send_error}</span>{:else}—{/if}
                    </td>
                    <td class="probe-emailid" title={p.smtp2go_email_id || ''}>{p.smtp2go_email_id || '—'}</td>
                  </tr>
                {/each}
              </tbody>
            </table>
          </div>
        {/if}
      </section>

      {#if rowMenu.open}
        <button class="probe-menu-backdrop" aria-label="Close menu" onclick={closeRowMenu}></button>
        <div class="probe-menu" style="left:{rowMenu.x}px; top:{rowMenu.y}px" role="menu">
          <button role="menuitem" onclick={() => copyRowAs(rowMenu.probe, 'json')}>
            <span class="material-symbols-outlined text-base align-middle">content_copy</span> Copy as JSON
          </button>
          <button role="menuitem" onclick={() => copyRowAs(rowMenu.probe, 'csv')}>
            <span class="material-symbols-outlined text-base align-middle">content_copy</span> Copy as CSV
          </button>
          <button role="menuitem" onclick={() => downloadRowAs(rowMenu.probe, 'json')}>
            <span class="material-symbols-outlined text-base align-middle">download</span> Download JSON
          </button>
          <button role="menuitem" onclick={() => downloadRowAs(rowMenu.probe, 'csv')}>
            <span class="material-symbols-outlined text-base align-middle">download</span> Download CSV
          </button>
        </div>
      {/if}
    {/if}
  {:else if activeTab === 'users'}
    <Users />
  {/if}
{/if}

<style>
  /* M3 secondary tabs: a horizontal strip with text + icon, separated
     from content by a hairline outline-variant divider. The active tab
     carries a 3dp primary-color indicator at its bottom edge that
     overlaps the divider. */
  .settings-tabs {
    display: flex;
    gap: 0;
    margin-block-end: 16px;
    border-block-end: 1px solid rgb(var(--md-sys-color-outline-variant));
  }
  .settings-tab {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    block-size: 48px;
    padding-inline: 24px;
    border: 0;
    background: transparent;
    color: rgb(var(--md-sys-color-on-surface-variant));
    font-size: 0.875rem;
    font-weight: 500;
    letter-spacing: 0.006em;
    cursor: pointer;
    transition: background 120ms, color 120ms;
    flex-shrink: 0;
  }
  .settings-tab:hover {
    background: rgb(var(--md-sys-color-on-surface) / 0.06);
    color: rgb(var(--md-sys-color-on-surface));
  }
  .settings-tab::after {
    content: '';
    position: absolute;
    inset-block-end: -1px;
    inset-inline: 12px;
    block-size: 3px;
    border-radius: 3px 3px 0 0;
    background: transparent;
    transition: background 160ms;
  }
  .settings-tab-active { color: rgb(var(--md-sys-color-primary)); }
  .settings-tab-active::after { background: rgb(var(--md-sys-color-primary)); }
  .settings-tab-active:hover { background: rgb(var(--md-sys-color-primary) / 0.08); }

  .settings-card-head {
    display: flex;
    gap: 0.75rem;
    align-items: flex-start;
    margin-block-end: 1rem;
  }
  .settings-card-title { font-size: 16px; font-weight: 500; margin: 0; }
  .settings-card-sub {
    font-size: 13px;
    color: rgb(var(--md-sys-color-on-surface-variant));
    margin: 4px 0 0;
  }
  .settings-link { color: rgb(var(--md-sys-color-primary)); text-decoration: underline; }

  .settings-field-hint {
    font-size: 12px;
    color: rgb(var(--md-sys-color-on-surface-variant));
    margin: 6px 2px 0;
    display: flex;
    align-items: center;
    gap: 4px;
  }
  .settings-field-hint .material-symbols-outlined {
    font-size: 15px;
    color: rgb(var(--md-sys-color-primary));
  }

  .settings-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 0.75rem;
  }
  .settings-grid-wide { grid-column: 1 / -1; }

  .settings-textarea-label {
    display: block;
    font-size: 12px;
    color: rgb(var(--md-sys-color-on-surface-variant));
  }
  .settings-textarea-label > span { display: block; margin-block-end: 4px; }
  .settings-textarea {
    inline-size: 100%;
    min-block-size: 80px;
    padding: 0.625rem 0.75rem;
    border: 1px solid rgb(var(--md-sys-color-outline));
    border-radius: 8px;
    background: rgb(var(--md-sys-color-surface));
    color: rgb(var(--md-sys-color-on-surface));
    font-family: inherit;
    font-size: 14px;
    resize: vertical;
  }
  .settings-textarea:focus {
    outline: none;
    border-color: rgb(var(--md-sys-color-primary));
    border-width: 2px;
    padding: calc(0.625rem - 1px) calc(0.75rem - 1px);
  }

  .settings-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
    margin-block-start: 1rem;
  }
  .settings-btn-text {
    padding: 0.5rem 1rem;
    border-radius: 999px;
    background: transparent;
    color: rgb(var(--md-sys-color-primary));
    font-size: 14px;
    font-weight: 500;
    border: 0;
    cursor: pointer;
  }
  .settings-btn-text:hover { background: rgb(var(--md-sys-color-primary) / 0.08); }
  .settings-btn-text:disabled { opacity: 0.5; cursor: not-allowed; }

  .settings-btn-primary {
    padding: 0.625rem 1.5rem;
    border-radius: 999px;
    background: rgb(var(--md-sys-color-primary));
    color: rgb(var(--md-sys-color-on-primary));
    font-size: 14px;
    font-weight: 500;
    border: 0;
    cursor: pointer;
  }
  .settings-btn-primary:hover { opacity: 0.9; }
  .settings-btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }

  /* button styled as an inline link (the "Alerts" jump in the probe blurb) */
  button.settings-link {
    background: none;
    border: 0;
    padding: 0;
    font: inherit;
    cursor: pointer;
  }

  .probe-check {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 14px;
    color: rgb(var(--md-sys-color-on-surface));
    cursor: pointer;
  }
  .probe-checkbox { inline-size: 18px; block-size: 18px; accent-color: rgb(var(--md-sys-color-primary)); }

  .probe-log-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
    flex: 1;
  }
  .probe-log-actions { display: flex; gap: 0.25rem; flex-shrink: 0; }
  /* On phone-width screens the action buttons would squeeze the description
     into a sliver of a column, so stack them under the heading instead. */
  @media (max-width: 640px) {
    .probe-log-head { flex-direction: column; gap: 0.5rem; }
    .probe-log-actions { align-self: flex-start; }
  }

  /* Summary card shown when a credential is registered (read-only view). */
  .settings-summary {
    border: 1px solid rgb(var(--md-sys-color-outline-variant));
    border-radius: 12px;
    padding: 14px 16px;
    background: rgb(var(--md-sys-color-on-surface) / 0.02);
  }
  .settings-summary-ok {
    display: flex;
    align-items: center;
    gap: 6px;
    margin: 0 0 10px;
    font-size: 14px;
    font-weight: 500;
    color: rgb(var(--md-sys-color-primary));
  }
  .settings-summary-ok .material-symbols-outlined { font-size: 18px; }
  .settings-summary-dl {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 10px 24px;
    margin: 0;
  }
  .settings-summary-dl dt {
    font-size: 12px;
    color: rgb(var(--md-sys-color-on-surface-variant));
    margin: 0 0 2px;
  }
  .settings-summary-dl dd {
    margin: 0;
    font-size: 14px;
    color: rgb(var(--md-sys-color-on-surface));
    overflow-wrap: anywhere;
  }

  /* Recent-probes selection bar. */
  .probe-selbar {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 8px 4px;
    margin-block-end: 6px;
  }
  .probe-selbar-count {
    font-size: 13px;
    font-weight: 500;
    color: rgb(var(--md-sys-color-primary));
    margin-inline-end: 0.25rem;
  }
  .probe-check-col { inline-size: 36px; text-align: center; }
  .probe-row-selected { background: rgb(var(--md-sys-color-primary) / 0.08); }
  .probe-muted { color: rgb(var(--md-sys-color-on-surface-variant)); }
  .probe-recipient,
  .probe-emailid {
    max-inline-size: 220px;
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: 12px;
    color: rgb(var(--md-sys-color-on-surface-variant));
  }
  .probe-emailid { font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace; }
  .probe-type {
    display: inline-block;
    padding: 1px 7px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    background: rgb(var(--md-sys-color-on-surface) / 0.08);
    color: rgb(var(--md-sys-color-on-surface-variant));
  }
  .probe-error {
    max-inline-size: 260px;
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: 12px;
  }
  .probe-error-text { color: rgb(var(--md-sys-color-error)); }
  /* Long-press should bring up our menu, not the native text-selection callout. */
  .probe-table tbody tr {
    -webkit-touch-callout: none;
    user-select: none;
  }

  .probe-menu-backdrop {
    position: fixed;
    inset: 0;
    z-index: 60;
    background: transparent;
    border: 0;
    padding: 0;
    cursor: default;
  }
  .probe-menu {
    position: fixed;
    z-index: 61;
    min-inline-size: 180px;
    padding: 6px;
    border-radius: 12px;
    background: rgb(var(--md-sys-color-surface));
    border: 1px solid rgb(var(--md-sys-color-outline-variant));
    box-shadow: 0 8px 24px rgb(0 0 0 / 0.28);
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
  .probe-menu button {
    display: flex;
    align-items: center;
    gap: 8px;
    inline-size: 100%;
    padding: 10px 12px;
    border: 0;
    border-radius: 8px;
    background: transparent;
    color: rgb(var(--md-sys-color-on-surface));
    font: inherit;
    font-size: 14px;
    text-align: start;
    cursor: pointer;
  }
  .probe-menu button:hover { background: rgb(var(--md-sys-color-on-surface) / 0.08); }
  .probe-menu .material-symbols-outlined { color: rgb(var(--md-sys-color-on-surface-variant)); }

  .probe-table-wrap { overflow-x: auto; }
  .probe-table {
    inline-size: 100%;
    border-collapse: collapse;
    font-size: 13px;
  }
  .probe-table th,
  .probe-table td {
    text-align: start;
    padding: 0.5rem 0.75rem;
    border-block-end: 1px solid rgb(var(--md-sys-color-outline-variant));
    white-space: nowrap;
  }
  .probe-table th {
    font-weight: 500;
    color: rgb(var(--md-sys-color-on-surface-variant));
  }
  .probe-table td { color: rgb(var(--md-sys-color-on-surface)); }
  .probe-sender {
    max-inline-size: 200px;
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: 12px;
    color: rgb(var(--md-sys-color-on-surface-variant));
  }

  .probe-badge {
    display: inline-block;
    padding: 0.125rem 0.5rem;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 500;
  }
  .probe-badge-ok   { background: rgb(var(--md-sys-color-primary) / 0.14); color: rgb(var(--md-sys-color-primary)); }
  .probe-badge-wait { background: rgb(var(--md-sys-color-on-surface) / 0.08); color: rgb(var(--md-sys-color-on-surface-variant)); }
  .probe-badge-fail { background: rgb(var(--md-sys-color-error) / 0.14); color: rgb(var(--md-sys-color-error)); }
</style>
